/**
 * Cloud Functions Service
 * Manages interactions with Firebase Cloud Functions
 */

import * as Util from "@utils/Util.js";
import { getFunctionsBaseUrl } from "./core/FirebaseConfig";
import { getUserIdToken } from "./AuthService";
import {
  createSuccessResponse,
  createErrorResponse,
  createRetryHandler,
} from "./core/ErrorHandler";

/**
 * Wrapper function that handles all error/success response formatting
 * Allows functions to just throw errors and return raw data
 * @param {string} functionName - Name of the function for error context
 * @param {Function} operation - The operation to execute
 * @returns {Promise<Object>} - Standardized response object
 */
async function withCloudFunction(functionName, operation) {
  try {
    const result = await operation();

    if (isStandardResponse(result)) {
      return normalizeStandardResponse(result);
    }

    return createSuccessResponse(result);
  } catch (error) {
    return createErrorResponse(error, functionName);
  }
}

function isStandardResponse(value) {
  return (
    value !== null &&
    typeof value === "object" &&
    Object.prototype.hasOwnProperty.call(value, "success") &&
    Object.prototype.hasOwnProperty.call(value, "data") &&
    Object.prototype.hasOwnProperty.call(value, "error")
  );
}

function normalizeStandardResponse(result) {
  return {
    success: !!result.success,
    data: typeof result.data === "undefined" ? null : result.data,
    error: normalizeErrorField(result.error),
  };
}

function normalizeErrorField(errorValue) {
  if (errorValue == null) {
    return null;
  }

  if (typeof errorValue === "string") {
    return errorValue;
  }

  if (errorValue instanceof Error) {
    return errorValue.message || "Unknown error occurred";
  }

  if (typeof errorValue === "object") {
    if (typeof errorValue.message === "string") {
      return errorValue.message;
    }

    try {
      return JSON.stringify(errorValue);
    } catch (serializationError) {
      console.warn(
        "Unable to serialize error payload returned by",
        serializationError
      );
    }
  }

  return String(errorValue);
}

/**
 * Create a shared wheel
 * @param {boolean} copyable - Whether the wheel is copyable
 * @param {Object} wheelConfig - Wheel configuration
 * @param {string} [idToken] - User ID token
 * @returns {Promise<Object>} - Response with path or error
 */
export async function createSharedWheel(copyable, wheelConfig, idToken = null) {
  return withCloudFunction("createSharedWheel", async () => {
    if (!wheelConfig) throw new Error("Wheel configuration is required");

    const token = idToken || (await getUserIdToken());

    // Ensure wheelConfig is properly serialized
    const configData = wheelConfig.getValues
      ? wheelConfig.getValues()
      : wheelConfig;
    if (
      !configData ||
      !configData.entries ||
      !Array.isArray(configData.entries)
    ) {
      throw new Error("Invalid wheel configuration: missing entries");
    }

    const payload = {
      copyable: copyable,
      wheelConfig: configData,
    };

    const response = await callFunction("createSharedWheel", "POST", payload);

    if (!response.success) {
      // Make sure we have a proper error message
      const errorMsg =
        response.error?.message ||
        response.error ||
        "Failed to create shared wheel";
      console.error("CreateSharedWheel error response:", errorMsg);
      throw new Error(errorMsg);
    }

    // Backend should return {path: "abc-def", managementToken: "mgmt-xxx-yyy"}
    if (!response.data || !response.data.path) {
      console.error("Invalid response from backend:", response.data);
      throw new Error("Invalid response from server: missing path");
    }

    return {
      path: response.data.path,
      managementToken: response.data.managementToken,
    };
  });
}

/**
 * Log a shared wheel read
 * @param {string} path - Wheel path
 * @returns {Promise<Object>} - Success response
 */
export async function logSharedWheelRead(path) {
  try {
    if (!path) {
      console.error("Cannot log shared wheel read: No path provided");
      return null; // Return null but do nothing
    }

    const payload = { path: path };
    await callFunction("logSharedWheelRead", "POST", payload);

    return true;
  } catch (error) {
    console.error("Error logging shared wheel read:", error);
    // Non-critical operation, so still return success
    return null;
  }
}

/**
 * Check if user is admin
 * @returns {Promise<Object>} - Response with admin status or error
 */
export async function userIsAdmin() {
  return withCloudFunction("userIsAdmin", async () => {
    const response = await callFunction("userIsAdmin", "GET", null);

    // If response failed, return admin=false for compatibility
    if (!response.success) {
      return { userIsAdmin: false };
    }

    return response.data;
  });
}

/**
 * Get a single shared wheel by path
 * @param {string} path - Wheel path
 * @returns {Promise<Object>} - Response with wheel data or error
 */
export async function getSharedWheel(path) {
  return withCloudFunction("getSharedWheel", async () => {
    if (!path) throw new Error("Wheel path is required");

    const response = await callFunction(`getSharedWheel/${path}`, "GET", null);
    return response.data;
  });
}

/**
 * Get all shared wheels for a user
 * @returns {Promise<Object>} - Response with wheels array or error
 */
export async function getSharedWheels() {
  return withCloudFunction("getSharedWheels", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to get shared wheels");

    const response = await callFunction("getSharedWheels", "GET", null);
    return response.data;
  });
}

/**
 * Delete a shared wheel
 * @param {string} path - Wheel path
 * @returns {Promise<Object>} - Response with remaining wheels or error
 */
export async function deleteSharedWheel(path) {
  return withCloudFunction("deleteSharedWheel", async () => {
    if (!path) throw new Error("Wheel path is required");

    const token = await getUserIdToken();
    if (!token)
      throw new Error("User must be logged in to delete shared wheels");

    const response = await callFunction(`deleteSharedWheel/${path}`, "DELETE");
    return response.data;
  });
}

/**
 * Get shared wheels by management tokens (for unauthenticated users)
 * @param {Array<string>} tokens - Array of management tokens
 * @returns {Promise<Object>} - Response with wheels array or error
 */
export async function getSharedWheelsByTokens(tokens) {
  return withCloudFunction("getSharedWheelsByTokens", async () => {
    if (!Array.isArray(tokens) || tokens.length === 0) {
      return [];
    }

    const payload = { tokens: tokens };
    const response = await callFunction(
      "getSharedWheelsByTokens",
      "POST",
      payload
    );
    return response.data;
  });
}

/**
 * Delete a shared wheel by management token (for unauthenticated users)
 * @param {string} token - Management token
 * @returns {Promise<Object>} - Success response or error
 */
export async function deleteSharedWheelByToken(token) {
  return withCloudFunction("deleteSharedWheelByToken", async () => {
    if (!token || !token.startsWith("mgmt-")) {
      throw new Error("Invalid management token");
    }

    const response = await callFunction(
      `deleteSharedWheelByToken/${token}`,
      "DELETE"
    );
    if (!response.success) throw new Error(response.error);

    return true;
  });
}

/**
 * Convert an anonymous account to a permanent account
 * @param {string} oldIdToken - Anonymous account token
 * @param {string} newIdToken - New account token
 * @returns {Promise<Object>} - Success response
 */
export async function convertAccount(oldIdToken, newIdToken) {
  return withCloudFunction("convertAccount", async () => {
    if (!oldIdToken || !newIdToken) {
      throw new Error("Both old and new ID tokens are required");
    }

    const payload = { oldIdToken: oldIdToken };
    const response = await callFunction("convertAccount", "POST", payload);

    if (!response.success) {
      console.error("Error converting account:", response.error);
      Util.trackException(response.error);
      return false; // Non-critical, return false instead of throwing
    }

    return true;
  });
}

/**
 * Delete a user account
 * @returns {Promise<Object>} - Success response or error
 */
export async function deleteAccount() {
  return withCloudFunction("deleteAccount", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to delete account");

    // Get current user UID from auth service
    const { getUid } = await import("./AuthService");
    const uid = await getUid();

    if (!uid) throw new Error("Unable to get user ID for account deletion");

    const response = await callFunction(`deleteAccount/${uid}`, "DELETE", null);
    if (!response.success) throw new Error(response.error);

    return response.data;
  });
}

/**
 * Get carousels (admin only)
 * @returns {Promise<Object>} - Response with carousels or error
 */
export async function getCarousels() {
  return withCloudFunction("getCarousels", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to access carousels");

    const response = await callFunction("getCarousels", "GET");
    return response.data;
  });
}

/**
 * Get number of wheels in review queue
 * @returns {Promise<Object>} - Response with count or error
 */
export async function getNumberOfWheelsInReviewQueue() {
  return withCloudFunction("getNumberOfWheelsInReviewQueue", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to check review queue");

    const response = await callFunction(
      "getNumberOfWheelsInReviewQueue",
      "GET",
      null
    );
    return response.data;
  });
}

/**
 * Translate entries
 * @param {Array} entries - Entries to translate
 * @returns {Promise<Object>} - Response with translations or error
 */
export async function translate(entries) {
  return withCloudFunction("translate", async () => {
    if (!Array.isArray(entries) || entries.length === 0) {
      throw new Error("Entries must be a non-empty array");
    }

    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to translate entries");

    const payload = { text: entries };
    const response = await callFunction("translate", "POST", payload);
    return response.data;
  });
}

/**
 * Get dirty words list (admin-only)
 * @returns {Promise<Object>} - Response with array of dirty words or error
 */
export async function getDirtyWords() {
  return withCloudFunction("getDirtyWords", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to get dirty words");

    const response = await callFunction("getDirtyWords", "GET");
    return response.data;
  });
}

/**
 * Set dirty words list (admin-only)
 * @param {Array} words - Array of words
 * @returns {Promise<Object>} - Success response or error
 */
export async function setDirtyWords(words) {
  return withCloudFunction("setDirtyWords", async () => {
    if (!Array.isArray(words)) throw new Error("Words must be an array");

    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to set dirty words");

    const payload = { words: words };
    const response = await callFunction("setDirtyWords", "PUT", payload);

    if (!response.success) throw new Error(response.error);
    return true;
  });
}

/**
 * Get earnings per review (admin-only)
 * @returns {Promise<Object>} - Response with earnings amount or error
 */
export async function getEarningsPerReview() {
  return withCloudFunction("getEarningsPerReview", async () => {
    const token = await getUserIdToken();
    if (!token)
      throw new Error("User must be logged in to get earnings per review");

    const response = await callFunction("getEarningsPerReview", "GET");
    return response.data;
  });
}

/**
 * Save carousel configuration (admin-only)
 * @param {Array} carousel - Carousel configuration
 * @returns {Promise<Object>} - Success response or error
 */
export async function saveCarousel(carousel) {
  return withCloudFunction("saveCarousel", async () => {
    if (!Array.isArray(carousel)) throw new Error("Carousel must be an array");

    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to save carousel");

    const payload = { carousel: carousel };
    const response = await callFunction("saveCarousel", "PUT", payload);

    if (!response.success) throw new Error(response.error);
    return true;
  });
}

/**
 * Reset admin session review count (admin-only)
 * @param {string} uid - Admin user ID (optional, defaults to current user)
 * @returns {Promise<Object>} - Success response or error
 */
export async function resetSessionReviews(uid = null) {
  return withCloudFunction("resetSessionReviews", async () => {
    const token = await getUserIdToken();
    if (!token)
      throw new Error("User must be logged in to reset session reviews");

    // Get current user UID if not provided
    const targetUid = uid || (await getUserUid());
    if (!targetUid) throw new Error("Unable to determine user ID");

    const response = await callFunction(
      `resetSessionReviews/${targetUid}`,
      "PUT",
      {}
    );

    if (!response.success) throw new Error(response.error);
    return true;
  });
}

/**
 * Get next shared wheel for review (admin-only)
 * @returns {Promise<Object>} - Response with wheel data or error
 */
export async function getNextSharedWheelForReview() {
  return withCloudFunction("getNextSharedWheelForReview", async () => {
    const token = await getUserIdToken();
    if (!token)
      throw new Error("User must be logged in to get next wheel for review");

    const response = await callFunction("getNextSharedWheelForReview", "GET");
    return response.data;
  });
}

// Helper function to get current user UID
async function getUserUid() {
  try {
    const { getUid } = await import("./AuthService");
    return await getUid();
  } catch (error) {
    console.error("Error getting user UID:", error);
    return null;
  }
}

/**
 * Get all administrators (admin-only)
 * @returns {Promise<Object>} - Response with admins array or error
 */
export async function getAdmins() {
  return withCloudFunction("getAdmins", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to get admins");

    const response = await callFunction("getAdmins", "GET");
    return response.data;
  });
}

/**
 * Delete an administrator (admin-only)
 * @param {string} uid - Admin user ID to delete
 * @returns {Promise<Object>} - Success response or error
 */
export async function deleteAdmin(uid) {
  return withCloudFunction("deleteAdmin", async () => {
    if (!uid) throw new Error("Admin UID is required");

    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to delete admin");

    const response = await callFunction(`deleteAdmin/${uid}`, "DELETE");
    if (!response.success) throw new Error(response.error);

    return true;
  });
}

/**
 * Add a new administrator (admin-only)
 * @param {string} firestoreId - Firestore ID for the new admin
 * @param {string} name - Name of the new admin
 * @returns {Promise<Object>} - Success response or error
 */
export async function addAdmin(firestoreId, name) {
  return withCloudFunction("addAdmin", async () => {
    if (!firestoreId || !name)
      throw new Error("Firestore ID and name are required");

    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to add admin");

    const payload = { firestoreId, name };
    const response = await callFunction("addAdmin", "POST", payload);
    return response.data;
  });
}

/**
 * Set admin's wheels count to zero (admin-only)
 * @param {string} uid - Admin user ID
 * @returns {Promise<Object>} - Success response or error
 */
export async function setAdminsWheelsToZero(uid) {
  return withCloudFunction("setAdminsWheelsToZero", async () => {
    if (!uid) throw new Error("Admin UID is required");

    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to reset admin wheels");

    const response = await callFunction(
      `setAdminsWheelsToZero/${uid}`,
      "PUT",
      {}
    );

    if (!response.success) throw new Error(response.error);
    return true;
  });
}

/**
 * Update administrator information (admin-only)
 * @param {string} uid - Admin user ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} - Success response or error
 */
export async function updateAdmin(uid, updateData) {
  return withCloudFunction("updateAdmin", async () => {
    if (!uid) throw new Error("Admin UID is required");

    if (!updateData || typeof updateData !== "object") {
      throw new Error("Update data is required");
    }

    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to update admin");

    const response = await callFunction(
      `updateAdmin/${uid}`,
      "PUT",
      updateData
    );
    return response.data;
  });
}

/**
 * Get payment history (admin-only)
 * @returns {Promise<Object>} - Response with payments array or error
 */
export async function getPayments() {
  return withCloudFunction("getPayments", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to get payments");

    const response = await callFunction("getPayments", "GET");
    return response.data;
  });
}

/**
 * Process shared wheel reads (admin-only)
 * Processes read records and populates the review queue
 * @returns {Promise<Object>} - Success response or error
 */
export async function processSharedWheelReads() {
  return withCloudFunction("processSharedWheelReads", async () => {
    const token = await getUserIdToken();
    if (!token)
      throw new Error("User must be logged in to process shared wheel reads");

    const response = await callFunction("processSharedWheelReads", "POST", {});
    return response.data;
  });
}

/**
 * Approve a shared wheel for review (admin-only)
 * @param {string} path - Wheel path
 * @returns {Promise<Object>} - Success response or error
 */
export async function approveSharedWheelForReview(path) {
  return withCloudFunction("approveSharedWheelForReview", async () => {
    if (!path) throw new Error("Wheel path is required");

    const token = await getUserIdToken();
    if (!token)
      throw new Error("User must be logged in to approve shared wheels");

    const response = await callFunction(
      `approveSharedWheelForReview/${path}`,
      "PUT",
      {}
    );
    return response.data;
  });
}

/**
 * Reject a shared wheel for review (admin-only)
 * @param {string} path - Wheel path
 * @returns {Promise<Object>} - Success response or error
 */
export async function rejectSharedWheelForReview(path) {
  return withCloudFunction("rejectSharedWheelForReview", async () => {
    if (!path) throw new Error("Wheel path is required");

    const token = await getUserIdToken();
    if (!token)
      throw new Error("User must be logged in to reject shared wheels");

    const response = await callFunction(
      `rejectSharedWheelForReview/${path}`,
      "DELETE",
      {}
    );
    return response.data;
  });
}

/**
 * Get user's saved wheels
 * @returns {Promise<Object>} - Response with wheels array or error
 */
export async function getUserProfile() {
  return withCloudFunction("getUserProfile", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to get profile");

    const response = await callFunction("getUserProfile", "GET");
    return response.data;
  });
}

/**
 * Upsert (create/update) the authenticated user's profile document
 * Ensures users/{uid} exists and updates timestamps/provider info
 * @returns {Promise<Object>} - Response data with { uid, exists: true }
 */
export async function upsertUserProfile() {
  return withCloudFunction("upsertUserProfile", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to upsert profile");

    const response = await callFunction("upsertUserProfile", "POST", {});
    return response.data;
  });
}

/**
 * Get user's saved wheels
 * @returns {Promise<Object>} - Response with wheels array or error
 */
export async function getUserWheels() {
  return withCloudFunction("getUserWheels", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to get wheels");

    const response = await callFunction("getUserWheels", "GET");
    return response.data;
  });
}

/**
 * Save a wheel to user's account
 * @param {Object} wheelConfig - Wheel configuration to save
 * @returns {Promise<Object>} - Success response or error
 */
export async function saveUserWheel(wheelConfig) {
  return withCloudFunction("saveUserWheel", async () => {
    if (!wheelConfig) throw new Error("Wheel configuration is required");

    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to save wheels");

    // Ensure wheelConfig is properly serialized
    const configData = wheelConfig.getValues
      ? wheelConfig.getValues()
      : wheelConfig;

    const payload = { wheelConfig: configData };
    const response = await callFunction("saveUserWheel", "POST", payload);
    return response.data;
  });
}

/**
 * Delete a saved wheel from user's account
 * @param {string} wheelTitle - Title of the wheel to delete
 * @returns {Promise<Object>} - Success response or error
 */
export async function deleteUserWheel(wheelTitle) {
  return withCloudFunction("deleteUserWheel", async () => {
    if (!wheelTitle) throw new Error("Wheel title is required");

    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to delete wheels");

    const payload = { wheelTitle: wheelTitle };
    const response = await callFunction("deleteUserWheel", "DELETE", payload);
    return response.data;
  });
}

/**
 * Log user activity for analytics
 * @returns {Promise<Object>} - Success response or error
 */
export async function logUserActivity() {
  return withCloudFunction("logUserActivity", async () => {
    const token = await getUserIdToken();
    if (!token) {
      // Non-critical operation, silently return success for unauthenticated users
      return true;
    }

    const response = await callFunction("logUserActivity", "POST", {});
    return response.data;
  });
}

/**
 * Log wheel read activity for analytics
 * @param {string} wheelTitle - Title of the wheel that was read
 * @returns {Promise<Object>} - Success response or error
 */
export async function logWheelRead(wheelTitle) {
  return withCloudFunction("logWheelRead", async () => {
    if (!wheelTitle) {
      // Non-critical operation, silently return for missing title
      return true;
    }

    const token = await getUserIdToken();
    if (!token) {
      // Non-critical operation, silently return success for unauthenticated users
      return true;
    }

    const payload = { wheelTitle: wheelTitle };
    const response = await callFunction("logWheelRead", "POST", payload);
    return response.data;
  });
}

/**
 * Get user's management tokens
 * @returns {Promise<Object>} - Response with tokens array or error
 */
export async function getUserTokens() {
  return withCloudFunction("getUserTokens", async () => {
    const token = await getUserIdToken();
    if (!token) throw new Error("User must be logged in to get tokens");

    const response = await callFunction("getUserTokens", "GET");
    return response.data;
  });
}

/**
 * Add a management token to user's profile
 * @param {string} token - Management token to add
 * @returns {Promise<Object>} - Success response or error
 */
export async function addUserToken(token) {
  return withCloudFunction("addUserToken", async () => {
    if (!token) throw new Error("Token is required");

    const idToken = await getUserIdToken();
    if (!idToken) throw new Error("User must be logged in to add tokens");

    const payload = { token: token };
    const response = await callFunction("addUserToken", "POST", payload);
    return response.data;
  });
}

/**
 * Remove a management token from user's profile
 * @param {string} token - Management token to remove
 * @returns {Promise<Object>} - Success response or error
 */
export async function removeUserToken(token) {
  return withCloudFunction("removeUserToken", async () => {
    if (!token) throw new Error("Token is required");

    const idToken = await getUserIdToken();
    if (!idToken) throw new Error("User must be logged in to remove tokens");

    const payload = { token: token };
    const response = await callFunction("removeUserToken", "DELETE", payload);
    return response.data;
  });
}

// Define function call with retries for network-related errors
const MAX_RETRIES = 2; // Maximum number of retries for transient errors
const BASE_DELAY = 200; // Base delay in ms between retries (will be multiplied by 2^retry)

/**
 * Determines if an error is retryable
 * @param {Error} error - The error to check
 * @returns {boolean} - Whether the error is retryable
 */
function isRetryableError(error) {
  // Network errors and server errors are retryable
  return (
    (error.message &&
      (error.message.includes("Network request failed") ||
        error.message.includes("unavailable") ||
        error.message.includes("timeout"))) ||
    // HTTP 5xx errors and some 4xx errors are retryable
    (error.status &&
      (error.status >= 500 ||
        error.status === 429 || // Too Many Requests
        error.status === 408)) // Request Timeout
  );
}

/**
 * Call a Firebase function with standardized error handling
 * @param {string} functionPath - Function path
 * @param {string} method - HTTP method
 * @param {Object} payload - Request payload
 * @param {string} idToken - Authentication token
 * @returns {Promise<Object>} - Standardized response object
 */
const callFunction = createRetryHandler(
  async (functionPath, method, payload = null) => {
    try {
      // Build the function URL
      const baseUrl = getFunctionsBaseUrl();
      const url = `${baseUrl}/${functionPath}`;

      // Prepare request options
      const options = {
        method: method,
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "omit", // Don't send cookies for cross-origin requests
      };

      // Get a fresh token for each attempt
      const token = await getUserIdToken();

      // Add authorization if token is provided
      if (token) {
        options.headers["Authorization"] = token;
      } else {
      }

      // Add payload for non-GET requests
      if (payload && method !== "GET") {
        options.body = JSON.stringify(payload);
      }

      console.debug(`Calling function ${functionPath} with method ${method}`);

      const response = await fetch(url, options);

      // Handle HTTP errors
      if (!response.ok) {
        console.error(
          `Error status: ${response.status} ${response.statusText}`
        );

        // Create an error with status for retry logic
        const error = new Error();
        error.status = response.status;

        // Handle different types of errors
        if (response.status === 0 || response.status === 404) {
          error.message = `Cannot connect to the server at ${url}. Make sure Firebase emulators are running if in development mode.`;
          throw error;
        } else if (response.status === 401 || response.status === 403) {
          error.message = "Authentication failed. Please log in again.";
          throw error;
        }

        // Clone response to avoid "body stream already read" error
        const responseClone = response.clone();

        try {
          // Try to parse as JSON first
          const errorData = await response.json();
          error.message =
            errorData.error || `Server responded with ${response.status}`;
          throw error;
        } catch (jsonError) {
          // If not JSON, get as text from the cloned response
          try {
            const errorText = await responseClone.text();
            error.message = `Server responded with ${response.status}: ${errorText}`;
          } catch (textError) {
            error.message = `Server responded with ${response.status}`;
          }
          throw error;
        }
      }

      // Parse response
      const data = await response.json();

      // Check for explicit error in response using our standardized format
      if (data && data.success === false) {
        return createErrorResponse(new Error(data.error), functionPath);
      }

      // If already in standardized format, return directly (no double-wrapping)
      if (
        data &&
        data.hasOwnProperty("success") &&
        data.hasOwnProperty("data") &&
        data.hasOwnProperty("error")
      ) {
        return data;
      }

      // Otherwise wrap raw data (for legacy endpoints)
      return createSuccessResponse(data);
    } catch (error) {
      // Special handling for CORS errors which often have no status code
      if (
        error.message &&
        (error.message.includes("blocked by CORS") ||
          error.message.includes("NetworkError") ||
          error.message.includes("Network request failed"))
      ) {
        console.error(
          "This appears to be a CORS issue. Check that Firebase emulators are running and configured correctly."
        );
        return createErrorResponse(
          new Error(
            "Cannot connect to service due to CORS restrictions. Please try again later."
          ),
          functionPath
        );
      }

      return createErrorResponse(error, functionPath);
    }
  },
  MAX_RETRIES,
  BASE_DELAY,
  isRetryableError
);
